<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    persistent
    hide-overlay
    transition="dialog-bottom-transition"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-btn
        icon
        light
        v-on="on"
        :class="{ 'primary-font-color': isTransparent }"
        :color="isTransparent ? '' : 'primary'"
        @click="userOpensShoppingList"
      >
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon v-bind="attrs" v-on="on"> receipt_long </v-icon>
          </template>
          <span>{{ $translate('SHOPPING_LISTS_AND_SUBSCRIPTIONS') }}</span>
        </v-tooltip>
      </v-btn>
    </template>

    <v-card tile>
      <ShoppingListsHeader
        @close="setDialog(false)"
        @take-subscription="onCreateSubscription"
        @create-list="onCreateShoppingList"
      />

      <ShoppingListsNoData
        v-if="localShoppingLists.length === 0"
        class="py-12"
        @take-subscription="onCreateSubscription"
        @create-list="onCreateShoppingList"
      />

      <v-card
        v-else-if="selectedShoppingList >= 0"
        flat
        elevation="0"
        class="body__background-color"
      >
        <v-card-title class="text-center justify-center py-6">
          <h2 class="font-weight-bold text-break">
            {{ localShoppingLists[selectedShoppingList].ShoppingListName }}
          </h2>
          <v-btn
            icon
            v-if="isShoppingListAdmin(localShoppingLists[selectedShoppingList])"
            @click="
              userWantsToEditName(localShoppingLists[selectedShoppingList])
            "
            class="ml-2"
          >
            <v-icon color="primary" large>mode_edit</v-icon>
          </v-btn>
        </v-card-title>
        <v-row>
          <v-col cols="12" md="4"></v-col>
          <v-col cols="12" md="4" class="d-flex justify-center align-center">
            <div class="text-body-1">
              {{ $translate('TYPE') }}:
              {{ localShoppingLists[selectedShoppingList].Type }}
            </div>
          </v-col>
          <v-col cols="12" md="4">
            <v-btn @click="getShoppingLists" color="secondary">
              <v-icon>mdi-refresh</v-icon>
              {{ $translate('REFRESH_SHOPPINGLISTS') }}
            </v-btn>
          </v-col>
        </v-row>

        <v-tabs
          v-model="selectedShoppingList"
          background-color="transparent"
          color="primary"
          grow
          center-active
          show-arrows
        >
          <v-tab
            v-for="(item, index) in localShoppingLists"
            :key="index"
            style="text-transform: none"
          >
            {{ item.ShoppingListName }}
          </v-tab>
        </v-tabs>

        <v-tabs-items v-model="selectedShoppingList">
          <v-tab-item
            v-for="(item, itemIndex) in localShoppingLists"
            :key="itemIndex"
            transition="slide-x-transition"
          >
            <v-card class="transparent" elevation="0">
              <v-row justify="center" class="ma-0">
                <v-col cols="12" lg="6" class="pa-2">
                  <h2 class="text-center font-weight-bold mb-2">
                    {{ $translate('PRODUCTS') }}
                  </h2>

                  <v-card class="pa-4" outlined color="grey lighten-3">
                    <ShoppingListProducts
                      class="mb-2"
                      :shoppingList="item"
                      @add="() => onAddProduct(item)"
                      @item-change="onChangeProductItem"
                      @decrease="
                        (product) =>
                          onAddOrSubtractsProduct('subtracts', product)
                      "
                      @increase="
                        (product) => onAddOrSubtractsProduct('add', product)
                      "
                      @delete="(product) => onRemoveProduct(product)"
                    />

                    <ShoppingListDiscounts
                      :discounts="getDiscountItems(item)"
                    />

                    <!--                    <ShoppingListTotalPrice :selectedShoppingList="item" />-->

                    <v-row justify="end">
                      <v-col cols="12" sm="7" md="6">
                        <!-- Subtotal price -->
                        <v-row
                          v-if="isProfUser && item.Products.length > 0"
                          justify="space-between"
                          class="ma-0 text-subtitle-2"
                        >
                          <div>{{ $translate('SUBTOTAL') }}</div>
                          <div>
                            {{ formatter.format(getSubtotalPrice(item)) }}
                          </div>
                        </v-row>

                        <!-- Delivery fee -->
                        <v-row
                          v-if="
                            item.Products.length > 0 &&
                            isDeliveryDetailsValid(item)
                          "
                          justify="space-between"
                          class="ma-0 text-subtitle-2"
                        >
                          <div>{{ $translate('DELIVERY_FEE') }}</div>
                          <div>
                            {{ formatter.format(getListDeliveryPrice(item)) }}
                          </div>
                        </v-row>

                        <!-- Pickup fee -->
                        <v-row
                          v-if="showPickupPrice"
                          justify="space-between"
                          class="ma-0 text-subtitle-2"
                        >
                          <div>{{ $translate('PICKUP_FEE') }}</div>
                          <div>
                            {{ formatter.format(pickupPrice) }}
                          </div>
                        </v-row>

                        <!-- Discount -->
                        <v-row
                          v-if="Number(getTotalDiscount(item)).toFixed(2) < 0"
                          justify="space-between"
                          class="ma-0 text-subtitle-2"
                        >
                          <div>{{ $translate('DISCOUNT') }}</div>
                          <div class="red--text">
                            {{ formatter.format(getTotalDiscount(item)) }}
                          </div>
                        </v-row>

                        <!-- VAT info for the professional user -->
                        <v-row
                          v-if="isProfUser && item.Products.length > 0"
                          justify="space-between"
                          class="ma-0 text-subtitle-2"
                        >
                          <div>{{ $translate('VAT') }}</div>
                          <div>
                            {{ formatter.format(getTotalVATPrice(item)) }}
                          </div>
                        </v-row>

                        <!-- Deposit value -->
                        <v-row
                          v-if="getDepositValue(item) > 0"
                          justify="space-between"
                          class="ma-0 text-subtitle-2"
                        >
                          <div>{{ $translate('DEPOSIT_VALUE') }}</div>
                          <div>
                            {{ formatter.format(getDepositValue(item)) }}
                          </div>
                        </v-row>
                        <!-- Total price -->
                        <v-row
                          v-if="item.Products.length > 0"
                          justify="space-between"
                          class="ma-0 text-h6"
                        >
                          <div>{{ $translate('LINDA_TOTAL_PRICE') }}</div>
                          <div>
                            {{
                              formatter.format(getShoppingListTotalPrice(item))
                            }}
                          </div>
                        </v-row>
                      </v-col>
                    </v-row>

                    <!-- Alert in case minimum value is not reached -->
                    <v-alert
                      v-if="showMinSubscriptionValue(item)"
                      type="warning"
                      class="mt-3"
                      transition="expand-transition"
                    >
                      {{ $translate('MIN_SUBSCRIPTION_VALUE_ALERT') }}:
                      <span class="font-weight-bold">
                        {{ formatter.format(minSubscriptionValue) }}
                      </span>
                    </v-alert>

                    <v-alert
                      class="mt-4"
                      type="info"
                      v-if="
                        item.DeliveryFee != null &&
                        item.DeliveryFee.DeliveryError &&
                        item.Type !== 'Subscription'
                      "
                    >
                      {{ item.DeliveryFee.DeliveryText }}
                    </v-alert>

                    <v-alert
                      v-if="minPickupFeeAlert"
                      class="mt-4"
                      type="warning"
                    >
                      {{ minPickupFeeAlert }}
                    </v-alert>

                    <!-- save as default shopping list and recover default list-->
                    <v-row justify="center" class="mt-4">
                      <template
                        v-if="
                          item.Type !== 'WishList' && isShoppingListAdmin(item)
                        "
                      >
                        <v-btn
                          color="primary"
                          class="text-capitalize mx-1 mb-2 primary-font-color"
                          :loading="updatingSubscription"
                          @click="onUpdateSubscription"
                        >
                          {{ $translate('UPDATE_SUBSCRIPTION') }}
                        </v-btn>
                        <v-btn
                          color="primary"
                          class="text-capitalize mx-1 mb-2 primary-font-color"
                          :loading="resetingSubscription"
                          @click="onResetSubscription"
                        >
                          {{ $translate('RESET_SUBSCRIPTION') }}
                        </v-btn>
                      </template>

                      <!-- Button to copy list to cart -->
                      <v-btn
                        v-if="
                          item.Temporary &&
                          item.Products.length > 0 &&
                          isShoppingListAdmin(item)
                        "
                        color="primary"
                        class="text-capitalize mx-1 mb-2 primary-font-color"
                        @click="orderShoppingListNow(item)"
                      >
                        {{ $translate('COPY_TO_SHOPPINGCART') }}
                      </v-btn>
                    </v-row>
                    <!--suggestions-->
                    <v-col v-if="suggestions.length > 0" class="pa-0 mt-5">
                      <div class="text-h6 mb-2">
                        {{ $translate('SUGGESTIONS') }}
                      </div>
                      <Suggestions
                        :suggestions="suggestions"
                        :cols="5"
                        :sm="4"
                        :md="3"
                        :lg="3"
                        class="px-3"
                        @click="onSuggestionClick(item)"
                      />
                    </v-col>
                  </v-card>
                </v-col>

                <v-col cols="12" lg="6" class="pa-2">
                  <h2 class="text-center font-weight-bold mb-2">
                    {{ $translate('DETAILS') }}
                  </h2>

                  <v-card
                    class="pa-4"
                    style="min-height: 15em"
                    color="grey lighten-3"
                    flat
                  >
                    <ShoppingListUsers
                      :shoppingList="item"
                      @add-user="userWantsToAddUsers"
                      @delete-user="triggerDeleteUserFromMailList"
                      @leave="onLeaveList"
                    />

                    <!-- Show the selected PaymentMethod for this ShoppingList by the user -->
                    <v-card
                      v-if="selectedPaymentMethod.PaymentMethod"
                      class="mt-4 px-6 py-4"
                      outlined
                    >
                      <v-col class="pa-0">
                        <h2
                          :style="`color: ${$vuetify.theme.themes.light.primary}`"
                          class="mb-2"
                        >
                          {{ $translate('SELECTED_PAYMENT_METHOD') }}
                        </h2>
                        <v-row align="center" class="ma-0">
                          <v-img
                            :src="selectedPaymentMethod.PictureUrl"
                            max-width="60px"
                            max-height="60px"
                          />
                          <h4 class="text-center ml-6">
                            {{ selectedPaymentMethod.DisplayName }}
                          </h4>
                        </v-row>
                        <p class="mt-4">
                          {{ paymentMethodInfoText }}
                        </p>
                      </v-col>
                    </v-card>

                    <v-row justify="center" class="ma-0 mt-4">
                      <!-- Button to choose delivery date/time by admin -->
                      <v-btn
                        v-if="
                          isShoppingListAdmin(item) &&
                          item.Products.length &&
                          item.Temporary
                        "
                        color="primary"
                        class="text-capitalize mb-3 primary-font-color"
                        @click="makeShopListDefinite(item)"
                      >
                        {{ $translate('PICK_YOUR_MOMENT') }}
                      </v-btn>

                      <!-- Button to skip next schedule by admin -->
                      <v-btn
                        v-if="
                          isShoppingListAdmin(item) &&
                          item.Products.length &&
                          !item.Temporary &&
                          item.Frequency !== 0
                        "
                        color="secondary"
                        class="text-capitalize mr-2 mb-3"
                        :disabled="skipDeliveryBtnDisabled"
                        @click="onSkipDelivery"
                      >
                        {{ $translate('SKIP_DELIVERY') }}
                      </v-btn>
                      <!-- Button to change pickup/delivery -->
                      <v-btn
                        v-if="
                          isShoppingListAdmin(item) &&
                          item.Products.length &&
                          !item.Temporary
                        "
                        color="secondary"
                        class="text-capitalize mr-2 mb-3"
                        @click="changePickupDelivery(item)"
                      >
                        {{ $translate('CHANGE_DELIVERY') }}
                      </v-btn>
                      <!-- Button to pay scheduled list by admin -->
                      <v-btn
                        v-if="
                          item.Products.length &&
                          !item.Temporary &&
                          item.Type !== 'Subscription'
                        "
                        color="primary"
                        class="text-capitalize mb-3 primary-font-color"
                        @click="userWantsToPayShoppingList(item)"
                        :loading="loading"
                        :disabled="
                          item.DeliveryFee != null &&
                          item.DeliveryFee.DeliveryError
                        "
                      >
                        {{ $translate('PAY_NOW') }}
                      </v-btn>
                    </v-row>

                    <ShoppingListDetails :shoppingList="item" />

                    <v-row justify="center" class="ma-0 mt-3">
                      <!-- Button to cancel order -->
                      <v-btn
                        v-if="
                          isShoppingListAdmin(item) &&
                          item.Products.length &&
                          !item.Temporary &&
                          item.Type !== 'Subscription'
                        "
                        class="text-capitalize mr-2 mb-3"
                        color="secondary"
                        @click="triggerCancelOrderDialog(item)"
                      >
                        {{ $translate('CANCEL_ORDER') }}
                      </v-btn>
                      <!-- Button to delete list -->
                      <v-btn
                        v-if="isShoppingListAdmin(item)"
                        class="text-capitalize white--text"
                        color="red accent-3"
                        @click="triggerDeleteShoppingListDialog(item)"
                      >
                        {{ $translate('DELETE') }}
                      </v-btn>
                    </v-row>
                  </v-card>
                </v-col>
              </v-row>
            </v-card>
            <ShoppingList_Booking
              v-if="makeShopListDefiniteBoolean"
              :shoppingList="toUpShoppingList"
              :changePickupDelivery="isChangePickupDelivery"
              @closeShopListDefiniteDialog="closeShopListDefiniteDialog"
              @openSuccessDefiniteDialog="openSuccessDefiniteDialog"
            />
          </v-tab-item>
        </v-tabs-items>
      </v-card>

      <!-- Success dialog -->
      <SuccessDialog
        :dialog="successDialog"
        :successText="paymentMsg"
        @continue="goToMyActivities()"
        @back="
          successDialog = false;
          setDialog(false);
        "
      />

      <!-- Success definite dialog -->
      <v-dialog max-width="500" v-model="successDefiniteDialog" persistent>
        <v-card class="text-center">
          <v-card-title class="justify-center">
            <v-icon color="green" size="128">check_circle</v-icon>
          </v-card-title>
          <v-card-text
            style="word-break: normal"
            class="justify-center mt-5 pb-4"
          >
            <div class="mb-3">
              {{ $translate('SUCCESS_SELECTED_SHOPPING_LIST_MOMENT') }}
            </div>
            <div>
              {{ $translate('SUCCESS_SELECTED_SHOPPING_LIST_MOMENT_EXTRA') }}
            </div>
          </v-card-text>
          <v-card-actions class="pb-3">
            <v-btn
              color="primary"
              depressed
              block
              large
              @click="successDefiniteDialog = false"
            >
              {{ $translate('CLOSE') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog to confirm payment for the list -->
      <v-dialog
        hide-overlay
        v-model="userWantsToPayDialog"
        persistent
        max-width="500"
      >
        <v-card>
          <v-card-title class="headline" style="word-break: normal">
            {{ $translate('PAY_SHOPPING_LIST_TITLE') }}
          </v-card-title>
          <v-card-text style="word-break: normal">
            {{ $translate('PAY_SHOPPING_LIST_MESSAGE') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="secondary" text @click="userWantsToPayDialog = false">
              {{ $translate('NO') }}
            </v-btn>
            <v-btn
              color="primary"
              class="primary-font-color"
              text
              @click="payShoppingList()"
            >
              {{ $translate('YES') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog to cancel the order -->
      <v-dialog
        hide-overlay
        v-model="cancelOrderDialog"
        persistent
        max-width="500"
      >
        <v-card>
          <v-card-title class="headline" style="word-break: normal">
            <div>{{ $translate('CANCEL_SHOPPINGLIST') }}</div>
          </v-card-title>
          <v-card-text style="word-break: normal">
            <div>{{ $translate('SURE_CANCEL_SHOPPINGLIST') }}</div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              class="mr=4"
              color="secondary"
              text
              @click="cancelOrderDialog = false"
            >
              {{ $translate('NO') }}
            </v-btn>
            <v-btn
              color="primary"
              class="primary-font-color"
              text
              @click="cancelOrder()"
            >
              {{ $translate('YES') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog to delete the list -->
      <v-dialog
        hide-overlay
        v-model="deleteShoppingListDialog"
        persistent
        max-width="500"
      >
        <v-card>
          <v-card-title class="headline" style="word-break: normal">
            <div v-if="toDeleteShoppingList.Type === 'Subscription'">
              {{ $translate('DELETE_SUBSCRIPTION_TITLE') }}
            </div>
            <div v-else>{{ $translate('DELETE_SHOPPINGLIST') }}</div>
          </v-card-title>
          <v-card-text style="word-break: normal">
            <div v-if="toDeleteShoppingList.Type === 'Subscription'">
              {{ $translate('DELETE_SUBSCRIPTION_DESC') }}
            </div>
            <div v-else>{{ $translate('SURE_DELETE_SHOPPINGLIST') }}</div>
            <div class="font-weight-bold mt-1">
              {{ toDeleteShoppingList.ShoppingListName }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="secondary"
              text
              @click="deleteShoppingListDialog = false"
            >
              {{ $translate('CANCEL') }}
            </v-btn>
            <v-btn
              color="primary"
              class="primary-font-color"
              text
              @click="deleteShoppingList()"
            >
              {{ $translate('DELETE') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog to change list name -->
      <v-dialog
        hide-overlay
        v-model="changeNameDialog"
        persistent
        max-width="500"
      >
        <v-card>
          <v-card-title class="headline" style="word-break: normal">
            {{ $translate('CHANGE_SHOPPINGLIST_NAME') }}
          </v-card-title>
          <v-col cols="11">
            <v-text-field
              v-model="nameToChange"
              :label="$translate('SHOPPING_LIST_NAME')"
              outlined
              required
            />
          </v-col>
          <v-card-actions>
            <v-spacer />
            <v-btn color="secondary" text @click="changeNameDialog = false">
              {{ $translate('CLOSE') }}
            </v-btn>
            <v-btn
              color="primary"
              class="primary-font-color"
              text
              @click="saveShoppingList('exist')"
            >
              {{ $translate('SAVE') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog to add user to list -->
      <v-dialog hide-overlay v-model="addUserDialog" persistent max-width="500">
        <v-card>
          <v-card-title class="headline" style="word-break: normal">
            {{ $translate('ADD_USER_TO_SHOPPINGLIST') }}
          </v-card-title>
          <v-form ref="addUserForm">
            <v-col cols="11">
              <v-text-field
                v-model="addUserValue"
                :rules="emailRules"
                :label="$translate('ENTER_MAIL')"
                outlined
                required
              />
            </v-col>
          </v-form>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="secondary" text @click="addUserDialog = false">
              {{ $translate('CLOSE') }}
            </v-btn>
            <v-btn
              color="primary"
              class="primary-font-color"
              text
              @click="addUserToShoppingList"
            >
              {{ $translate('ADD_USER') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Dialog to confirm deleting a user -->
      <ConfirmDialog
        :visible="showDeleteUserDialog"
        :title="deleteUserDialogTitle"
        :message="deleteUserDialogMsg"
        :positiveBtnText="$translate('YES')"
        :negativeBtnText="$translate('NO')"
        @positiveBtnClicked="deleteUserFromMailList"
        @negativeBtnClicked="showDeleteUserDialog = false"
      />

      <!-- Dialog to confirm skipping next delivery -->
      <ConfirmDialog
        :visible="showSkipDeliveryDialog"
        :title="$translate('SKIP_DELIVERY_CONFIRM_TITLE')"
        :message="$translate('SKIP_DELIVERY_CONFIRM_MESSAGE')"
        :positiveBtnText="$translate('YES')"
        :negativeBtnText="$translate('NO')"
        @positiveBtnClicked="onSkipDeliveryConfirmed"
        @negativeBtnClicked="onSkipDeliveryCancelled"
      />

      <!-- Dialog to get user decision -->
      <ConfirmDialog
        :visible="showConfirmDialog"
        :title="confirmDialogTitle"
        :message="confirmDialogMsg"
        :positiveBtnText="confirmDialogPosBtnText"
        :negativeBtnText="confirmDialogNegBtnText"
        @positiveBtnClicked="onConfirmDialogPosBtnClick"
        @negativeBtnClicked="onConfirmDialogNegBtnClick"
      />

      <!-- Dialog to create new list -->
      <ShoppingListNewList
        :visible="showNewListDialog"
        @close="showNewListDialog = false"
        @saved="onNewListCreated"
      />

      <!-- Alert to inform user that the order start moment is not reached yet. -->
      <!-- After deadline passing, it takes an amount of time before user can order again. To prevent user orders just after deadline passing and orders for next week instead of this week. -->
      <!-- PayShoppingList will fail with errorCode E_SL_023. -->
      <GenericAlert :dialog="orderStartMomentNotReachedDialog" :title="$translate('NOTIFICATION')" :text="orderStartMomentNotReachedMessage" :button-text="$translate('OK')" />

      <!-- Progress circular -->
      <v-overlay :class="showProgress ? '' : 'd-none'" absolute color="grey">
        <v-progress-circular indeterminate color="primary" />
      </v-overlay>
    </v-card>
  </v-dialog>
</template>

<script>
/* eslint-disable no-param-reassign */
import { mapGetters, mapState } from 'vuex';
import { cloneDeep } from 'lodash';
import doRequest from '@/api/dssRequest';
import utility, { scrollToElement } from '@/shared/utility';
import {
  getShoppingLists,
  getAwardedGrowthVitaminCards,
  changeProductOnReservation,
  removeProductFromReservation,
  updateShoppingListProductsToCurrentOrder,
  resetOrderToShoppingList,
  skipDelivery,
  isShoppingListAdmin,
  makeShoppingListTemporary,
  deleteShoppingList,
  payShoppingList,
  saveShoppingList,
  orderShoppingListNow,
} from '@/objects/ShoppingList';
import {
  isWeightedProduct,
  getSortedItemDefValues,
  getItemByDefs,
} from '@/objects/Product';
import PickupFeeJS from '@/objects/PickupFee';
import ShoppingListsHeader from '@/components/shoppingLists/ShoppingListsHeader';
import ShoppingListsNoData from '@/components/shoppingLists/ShoppingListsNoData';
import ConfirmDialog from '@/components/generalComponents/uiComponents/ConfirmDialog';
import Suggestions from '@/components/generalComponents/Suggestions';
import ShoppingListProducts from '@/components/shoppingLists/ShoppingListProducts';
import ShoppingListDiscounts from '@/components/shoppingLists/ShoppingListDiscounts';
import ShoppingListUsers from '@/components/shoppingLists/ShoppingListUsers';
import ShoppingListDetails from '@/components/shoppingLists/ShoppingListDetails';
import ShoppingList_Booking from './ShoppingList_Booking.vue';
import ShoppingListNewList from './ShoppingListNewList.vue';
import SuccessDialog from '../uiComponents/SuccessDialog.vue';
import PaymentMethod from '@/objects/PaymentMethod';
import GenericAlert from "@/components/generalComponents/uiComponents/GenericAlert.vue";

export default {
  name: 'ShoppingList',
  components: {
    GenericAlert,
    ShoppingList_Booking,
    ConfirmDialog,
    ShoppingListNewList,
    SuccessDialog,
    Suggestions,
    ShoppingListProducts,
    ShoppingListUsers,
    ShoppingListDetails,
    ShoppingListsHeader,
    ShoppingListsNoData,
    ShoppingListDiscounts,
  },
  data() {
    return {
      // Shopping list payment
      userWantsToPayDialog: false,
      toPayShoppingList: {},
      checkboxToClearDeliveryPickUpMoment: false,
      successDefiniteDialog: false,
      paymentMsg: '',
      successDialog: false,
      toUpShoppingList: {},
      makeShopListDefiniteBoolean: false,
      loading: false,
      idWithChangeName: '',
      toDeleteUserFromMailList: {},
      deleteShoppingListDialog: false,
      toDeleteShoppingList: {},
      cancelOrderDialog: false,
      toCancelShoppingList: {},
      nameToChange: '',
      changeNameDialog: false,
      addUserDialog: false,
      addUserValue: '',
      dialog: false,
      selectedShoppingList: -1,
      showMenu: false,
      formatter: new Intl.NumberFormat('nl', {
        style: 'currency',
        currency: 'EUR',
      }),
      emailRules: [
        (v) => !!v || this.$translate('EMAIL_REQUIRED'),
        (v) => /.+@.+\..+/.test(v) || this.$translate('EMAIL_VALID'),
      ],

      skipDeliveryBtnDisabled: false,
      showProgress: false,

      showDeleteUserDialog: false,
      deleteUserDialogTitle: '',
      deleteUserDialogMsg: '',

      showSkipDeliveryDialog: false,
      showNewListDialog: false,

      localShoppingLists: [],
      suggestions: [],

      editSubscriptionProducts: false,

      showConfirmDialog: false,
      confirmDialogTitle: '',
      confirmDialogMsg: '',
      confirmDialogPosBtnText: '',
      confirmDialogNegBtnText: '',
      confirmDialogPosBtnHandler: undefined,
      confirmDialogNegBtnHandler: undefined,

      updatingSubscription: false,
      resetingSubscription: false,

      changeItemTimeOut: 0.3 * 1000,

      pickupFee: null,
      minPickupFeeAlert: '',
      selectedPaymentMethod: new PaymentMethod(),
      isChangePickupDelivery: false,

      orderStartMomentNotReachedMessage: '',
      orderStartMomentNotReachedDialog: false,
    };
  },
  props: {
    isTransparent: Boolean,
  },
  computed: {
    ...mapState('shoppingList', {
      shoppingLists: (state) => state.orders,
    }),
    ...mapGetters({
      currentSelectedShoppingList: 'getCurrentSelectedShoppingList',
      isProfUser: 'isProfUser',
    }),
    minSubscriptionValue() {
      let minValue = 0;
      const pgInfo = this.$store.getters['Account/partnerGroupDetails'];
      if (pgInfo != null && pgInfo.SubscriptionMinimumOrderValue != null) {
        minValue = pgInfo.SubscriptionMinimumOrderValue;
      }
      return minValue;
    },
    userId() {
      return this.$store.getters['auth/userName'];
    },
    userLoggedIn() {
      return this.$store.getters['auth/isValid'];
    },
    partnerGroupDetails() {
      return this.$store.getters['Account/partnerGroupDetails'];
    },
    showSubscibeBtn() {
      if (
        this.partnerGroupDetails != null &&
        this.partnerGroupDetails.ShowSubscriptionButton
      ) {
        return true;
      }

      return false;
    },
    showPickupPrice() {
      return (
        this.pickupFee != null &&
        this.pickupFee.isPossible() &&
        this.pickupFee.hasPrice()
      );
    },
    pickupPrice() {
      if (!this.showPickupPrice) {
        return 0.0;
      }

      if (this.isProfUser) {
        return this.pickupFee.price - this.pickupFee.vatPrice;
      }
      return this.pickupFee.price;
    },
    paymentMethodInfoText() {
      if (this.currentSelectedShoppingList.Type !== 'Subscription') {
        return this.$translate('SELECTED_PAYMENT_METHOD_INFO_LIST');
      } else {
        return this.$translate('SELECTED_PAYMENT_METHOD_INFO_SUBSC');
      }
    },
  },
  watch: {
    selectedShoppingList() {
      if (this.hasValidSelection()) {
        const list = this.localShoppingLists[this.selectedShoppingList];
        this.$store.commit('setCurrentSelectedShoppingList', list);

        this.getDeliveryFee();
        this.getPickupFee();
        this.initPaymentMethod();

        this.getAwardedGrowthVitaminCardsForList();
      } else {
        this.$store.commit('setCurrentSelectedShoppingList', {});
      }
    },
    shoppingLists() {
      this.initLists();
    },
  },
  created() {
    window.EventBus.listen(
      'triggerNavigationDrawer',
      this.triggerNavigationDrawer
    );
    window.EventBus.listen('openShoppingListDialog', this.openDialog);
  },
  beforeDestroy() {
    window.EventBus.removeListener(
      'triggerNavigationDrawer',
      this.triggerNavigationDrawer
    );
    window.EventBus.removeListener('openShoppingListDialog', this.openDialog);
  },
  mounted() {
    // this.getShoppingLists();
    // this.initLists();
  },
  methods: {
    initLists() {
      this.localShoppingLists = [];
      if (this.shoppingLists != null) {
        this.localShoppingLists = cloneDeep(this.shoppingLists);
      }

      this.initAllProducts();
      this.initSelectedList();
    },
    initSelectedList() {
      if (this.hasValidSelection()) {
        this.getDeliveryFee();
        this.getPickupFee();
        this.initPaymentMethod();
        this.getAwardedGrowthVitaminCardsForList();
      }
    },
    initAllProducts() {
      this.localShoppingLists.forEach((list) => {
        this.initProducts(list);
      });
    },
    initProducts(list) {
      list.Products.forEach((product) => {
        this.initProduct(product);
      });
      this.initOrderProducts(list);
    },
    initOrderProducts(list) {
      /*
      Main purpose is to add missing ItemDefs for each product in the order of a shopping list
      Shopping list => Order => Product
      */
      if (list.OrderGroup != null && list.OrderGroup.Orders != null) {
        list.OrderGroup.Orders.forEach((order) => {
          order.Products.forEach((product) => {
            // Get product from Products array
            const prodInList = list.Products.find(
              (el) => el.ProductId === product.ProductId
            );

            if (prodInList != null) {
              product.Product = { ...prodInList.Product };

              const prodItem = prodInList.Product.Items.find(
                (el) => el.ItemId === product.ItemId
              );

              if (prodItem != null) {
                product.Capacity = prodItem.Capacity;
              }
            }

            product.OrderId = order.OrderId;
            product.EventId = order.EventId;
            product.ItemDef = product.Aspects;
            product.Price = product.ItemTotalPrice;

            this.initProduct(product);
          });
        });
      }
    },
    initProduct(product) {
      this.initProductItemDefs(product);

      // To keep track of quantity change
      product._oldQuantity = product.Quantity;
    },
    initProductItemDefs(product) {
      product.ItemDefObj = {};
      const item = product.Product.Items.find(
        (itemInList) => itemInList.Id === product.ItemId
      );
      if (item != null) {
        product.ItemDefObj = { ...item.ItemDef };
      }
    },
    userWantsToPayShoppingList(item) {
      this.toPayShoppingList = item;
      this.userWantsToPayDialog = true;
    },
    getDeliveryFee() {
      const shoppingList = this.localShoppingLists[this.selectedShoppingList];

      if (
        shoppingList == null ||
        shoppingList.Temporary ||
        (shoppingList.SubEventType !== 'Delivery' &&
          shoppingList.SubEventType !== 'Shiplee')
      ) {
        if (shoppingList != null) {
          shoppingList.DeliveryFee = {};
          shoppingList.DeliveryDetails = {};
        }
        return;
      }

      const payload = {
        PostalCode: shoppingList.PostalCode,
        CountryCode: shoppingList.Country,
        ShoppingListId: shoppingList.ShoppingListId,
      };

      doRequest(
        this.onGetDeliveryFeeResponse,
        this.onGetDeliveryFeeError,
        'getDeliveryFee',
        shoppingList.SubEventId,
        payload
      );
    },
    onGetDeliveryFeeResponse(response) {
      const deliveryDetails = { ...response.data };
      const deliveryInfo = {};
      if (response.data.DeliveryPossible) {
        deliveryInfo.DeliveryText = `${this.$translate(
          'DELIVERY_FEE'
        )}: ${this.formatter.format(response.data.Price)}`;
        deliveryInfo.DeliveryError = false;
        deliveryInfo.Price = response.data.Price;
      } else {
        if (
          response.data.ResultSet != null &&
          response.data.ResultSet.AdditonalOrderValueForDelivery != null
        ) {
          deliveryInfo.DeliveryText = this.$translate(
            'DELIVERY_NO_ENOUGH_PRODUCTS'
          ).replace(
            'price',
            this.formatter.format(
              response.data.ResultSet.AdditonalOrderValueForDelivery
            )
          );
        } else {
          deliveryInfo.DeliveryText = this.$translate(
            'NO_DELIVERY_FOR_POSTAL_CODE'
          );
        }
        deliveryInfo.DeliveryError = true;
      }

      const shoppingList = this.localShoppingLists[this.selectedShoppingList];
      this.$set(shoppingList, 'DeliveryFee', deliveryInfo);
      this.$set(shoppingList, 'DeliveryDetails', deliveryDetails);
    },
    onGetDeliveryFeeError() {
      const deliveryInfo = {};
      deliveryInfo.DeliveryText = this.$translate(
        'NO_DELIVERY_FOR_POSTAL_CODE'
      );
      deliveryInfo.DeliveryError = true;

      const shoppingList = this.localShoppingLists[this.selectedShoppingList];
      this.$set(shoppingList, 'DeliveryFee', deliveryInfo);
    },
    openSuccessDefiniteDialog() {
      this.successDefiniteDialog = true;
      this.getShoppingLists();
    },
    closeShopListDefiniteDialog() {
      this.toUpShoppingList = {};
      this.makeShopListDefiniteBoolean = false;
    },
    makeShopListDefinite(shopList) {
      this.toUpShoppingList = {};
      this.toUpShoppingList = shopList;
      this.makeShopListDefiniteBoolean = true;
      this.isChangePickupDelivery = false;
    },
    changePickupDelivery(shopList) {
      const type = this.localShoppingLists[this.selectedShoppingList].Type;
      console.log(type);
      this.toUpShoppingList = {};
      this.toUpShoppingList = shopList;
      this.makeShopListDefiniteBoolean = true;
      if (type === 'Subscription') {
        this.isChangePickupDelivery = true;
      } else {
        this.isChangePickupDelivery = false;
      }
    },
    getShoppingLists() {
      if (!this.userLoggedIn) {
        return;
      }
      this.showProgress = true;

      const payload = {};

      doRequest(
        (response) => {
          this.showProgress = false;

          const lists = response.data.ShoppingLists;
          this.$store.commit('shoppingList/setOrders', lists);

          if (lists.length <= 0) {
            this.$store.commit('setCurrentSelectedShoppingList', {});
          }

          if (Object.keys(this.currentSelectedShoppingList).length > 0) {
            // Has selected list, try to locate this list
            const currentIndex = lists.findIndex(
              (list) =>
                list.ShoppingListId ===
                this.currentSelectedShoppingList.ShoppingListId
            );

            if (currentIndex !== -1) {
              // Current list found, show current list
              this.selectedShoppingList = currentIndex;
            } else if (lists.length > 0) {
              // Current list not found, but user has lists, show the first one
              this.selectedShoppingList = 0;
            } else {
              this.selectedShoppingList = -1;
            }
          } else if (lists.length > 0) {
            this.selectedShoppingList = 0;
          } else {
            this.selectedShoppingList = -1;
          }
        },
        () => {
          this.showProgress = false;
        },
        'getShoppingLists',
        this.userId,
        payload
      );
    },
    // getShoppingLists() {
    //   if (!this.userLoggedIn) {
    //     return;
    //   }
    //   this.showProgress = true;
    //   try {
    //     const response = getShoppingLists();
    //     this.showProgress = false;
    //
    //     const lists = response.data.ShoppingLists;
    //     this.$store.commit('shoppingList/setOrders', lists);
    //
    //     if (lists.length <= 0) {
    //       this.$store.commit('setCurrentSelectedShoppingList', {});
    //
    //       if (Object.keys(this.currentSelectedShoppingList).length > 0) {
    //         // Has selected list, try to locate this list
    //         const currentIndex = lists.findIndex(
    //           (list) =>
    //             list.ShoppingListId ===
    //             this.currentSelectedShoppingList.ShoppingListId
    //         );
    //
    //         if (currentIndex !== -1) {
    //           // Current list found, show current list
    //           this.selectedShoppingList = currentIndex;
    //         } else if (lists.length > 0) {
    //           // Current list not found, but user has lists, show the first one
    //           this.selectedShoppingList = 0;
    //         } else {
    //           this.selectedShoppingList = -1;
    //         }
    //       } else if (lists.length > 0) {
    //         this.selectedShoppingList = 0;
    //       } else {
    //         this.selectedShoppingList = -1;
    //       }
    //     }
    //   } catch (error) {
    //     this.showProgress = false;
    //   }
    // },
    deleteUserFromMailList() {
      const payload = {
        ShoppingListId:
          this.localShoppingLists[this.selectedShoppingList].ShoppingListId,
        UserIdToRemove: this.toDeleteUserFromMailList.UserId,
      };
      doRequest(
        () => {
          this.toDeleteUserFromMailList = {};
          this.showDeleteUserDialog = false;
          this.getShoppingLists();
        },
        () => {
          this.showDeleteUserDialog = false;
        },
        'removeUserFromShoppingList',
        this.userId,
        payload
      );
    },
    triggerDeleteUserFromMailList(user) {
      this.toDeleteUserFromMailList = {};
      this.toDeleteUserFromMailList = user;
      this.deleteUserDialogTitle = this.$translate(
        'DELETE_USER_FROM_MAIL_LIST'
      );
      this.deleteUserDialogMsg = `${this.$translate(
        'SURE_DELETE_USER_FROM_MAIL_LIST'
      )} ${this.toDeleteUserFromMailList.Email}`;
      this.showDeleteUserDialog = true;
    },
    onLeaveList() {
      this.toDeleteUserFromMailList = {
        UserId: this.userId,
        Email: this.$store.getters['auth/user'].email,
      };

      this.deleteUserDialogTitle = this.$translate('LEAVE_SHARED_LIST_TITLE');
      this.deleteUserDialogMsg = this.$translate('LEAVE_SHARED_LIST_MSG');
      this.showDeleteUserDialog = true;
    },
    triggerDeleteShoppingListDialog(shoppingList) {
      this.toDeleteShoppingList = {};
      this.toDeleteShoppingList = shoppingList;
      this.deleteShoppingListDialog = true;
    },
    // deleteShoppingList() {
    //   this.showProgress = true;
    //   const payload = {
    //     ShoppingListId: this.toDeleteShoppingList.ShoppingListId,
    //   };
    //   doRequest(
    //     () => {
    //       this.showProgress = false;
    //       this.toDeleteShoppingList = {};
    //       this.deleteShoppingListDialog = false;
    //       this.$store.commit('setCurrentSelectedShoppingList', {});
    //       this.getShoppingLists();
    //     },
    //     () => {
    //       this.showProgress = false;
    //     },
    //     'deleteShoppingList',
    //     this.userId,
    //     payload
    //   );
    // },

    async deleteShoppingList() {
      this.showProgress = true;
      const payload = {
        ShoppingListId: this.toDeleteShoppingList.ShoppingListId,
      };
      try {
        await deleteShoppingList(payload);
        this.showProgress = false;
        this.toDeleteShoppingList = {};
        this.deleteShoppingListDialog = false;
        this.$store.commit('setCurrentSelectedShoppingList', {});
        this.getShoppingLists();
      } catch (error) {
        if (error.data.ErrorCode === 'E_SL_017') {
          this.alreadyDeletedList();
        }
        this.showProgress = false;
      }
    },
    alreadyDeletedList() {
      this.getShoppingLists();
      window.EventBus.fire('activateSnackBar', {
        text: this.$translate('SHOPPING_LIST_NO_LONGER_EXISTS'),
        color: 'error',
      });
      this.deleteShoppingListDialog = false;
    },
    triggerCancelOrderDialog(shoppingList) {
      this.toCancelShoppingList = {};
      this.toCancelShoppingList = shoppingList;
      this.cancelOrderDialog = true;
    },
    // cancelOrder() {
    //   const payload = {
    //     ShoppingListId: this.toCancelShoppingList.ShoppingListId,
    //   };
    //   doRequest(
    //     () => {
    //       this.toCancelShoppingList = {};
    //       this.cancelOrderDialog = false;
    //       this.getShoppingLists();
    //     },
    //     () => {},
    //     'makeShoppingListTemporary',
    //     this.userId,
    //     payload
    //   );
    // },
    async cancelOrder() {
      const payload = {
        ShoppingListId: this.toCancelShoppingList.ShoppingListId,
      };
      try {
        await makeShoppingListTemporary(payload);
        this.toCancelShoppingList = {};
        this.cancelOrderDialog = false;
        console.log('I am going to shopping list');
        this.getShoppingLists();
        console.log('I came back');
      } catch (error) {
        console.log('ERROR', error);
      }
    },
    // saveShoppingList(flow) {
    //   this.loading = true;
    //   const payload = {};
    //
    //   if (flow === 'create') {
    //     payload.ShoppingListId = '';
    //   } else {
    //     payload.ShoppingListId = this.idWithChangeName;
    //   }
    //
    //   payload.ShoppingListName = this.nameToChange;
    //
    //   doRequest(
    //     (response) => {
    //       this.$store.commit(
    //         'setCurrentSelectedShoppingList',
    //         response.data.ShoppingList
    //       );
    //       this.nameToChange = '';
    //       this.idWithChangeName = '';
    //       this.loading = false;
    //       this.getShoppingLists();
    //       if (this.changeNameDialog) this.changeNameDialog = false;
    //     },
    //     () => {
    //       this.loading = false;
    //     },
    //     'saveShoppingList',
    //     this.userId,
    //     payload
    //   );
    // },

    async saveShoppingList(flow) {
      this.loading = true;
      const payload = {};

      if (flow === 'create') {
        payload.ShoppingListId = '';
      } else {
        payload.ShoppingListId = this.idWithChangeName;
      }

      payload.ShoppingListName = this.nameToChange;

      try {
        const response = await saveShoppingList(payload);
        this.$store.commit(
          'setCurrentSelectedShoppingList',
          response.data.ShoppingList
        );
        this.nameToChange = '';
        this.idWithChangeName = '';
        this.loading = false;
        this.getShoppingLists();
        if (this.changeNameDialog) this.changeNameDialog = false;
      } catch (error) {
        this.loading = false;
        console.log('ERROR', error);
      }
    },

    userWantsToEditName(item) {
      this.nameToChange = item.ShoppingListName;
      this.idWithChangeName = item.ShoppingListId;
      this.changeNameDialog = true;
    },
    userWantsToAddUsers() {
      this.addUserDialog = true;
      setTimeout(() => {
        this.$refs.addUserForm.reset();
      }, 0);
    },
    addUserToShoppingList() {
      if (!this.$refs.addUserForm.validate()) {
        return;
      }
      const payload = {
        ShoppingListId:
          this.localShoppingLists[this.selectedShoppingList].ShoppingListId,
        Email: this.addUserValue,
      };
      doRequest(
        () => {
          this.addUserValue = '';
          this.addUserDialog = false;
          this.getShoppingLists();
        },
        () => {},
        'addUserToShoppingList',
        this.userId,
        payload
      );
    },

    async orderShoppingListNow(shoppingList) {
      // variable to add orders to payload
      const allEventsForOrder = {};
      // loop through ticket/rental orders and set them in the variable
      this.$store.getters['shoppingCart/orders'].forEach((orderItem) => {
        // to create index in array with that EventId
        if (!allEventsForOrder.hasOwnProperty(orderItem.EventId)) {
          allEventsForOrder[orderItem.EventId] = [];
        }
        // add the items to that index
        allEventsForOrder[orderItem.EventId].push(orderItem.OrderId);
      });

      const payload = {
        ShoppingListId: shoppingList.ShoppingListId,
        Reservations: allEventsForOrder || [],
      };

      this.showProgress = true;
      try {
        const response = await orderShoppingListNow(payload);
        this.showProgress = false;
        const arrayOfReservations = [];
        response.data.UpdatedReservations.forEach((reservation) => {
          const toStoreOrder = {
            EventId: Number(reservation.EventInfo.EventId),
            Picture: reservation.EventInfo.PictureUrl,
            Name: reservation.EventInfo.EventTranslationName,
            Date: new Date(),
            OrderId: reservation.MainOrder.OrderId,
            OrderTotalPrice: reservation.MainOrder.OrderTotalPrice,
            PriceCategories: reservation.MainOrder.PriceCategories,
            Products: reservation.MainOrder.Products,
          };

          if (reservation.EventInfo != null) {
            if (reservation.EventInfo.Type != null) {
              if (reservation.EventInfo.Type === 'Webshop') {
                toStoreOrder.ShoppingCartType = 'Product';
              } else if (reservation.EventInfo.Type === 'Rental') {
                toStoreOrder.ShoppingCartType = 'Rental';
              } else {
                toStoreOrder.ShoppingCartType = 'Ticket';
              }
            }
          } else if (
            reservation.EventType != null &&
            reservation.EventType === 'Rental'
          ) {
            toStoreOrder.ShoppingCartType = 'Rental';
          } else if (
            reservation.EventType != null &&
            reservation.EventType === 'Ticket'
          ) {
            toStoreOrder.ShoppingCartType = 'Ticket';
          } else if (
            reservation.EventType != null &&
            reservation.EventType === 'Product'
          ) {
            toStoreOrder.ShoppingcartType = 'Product';
          }
          arrayOfReservations.push(toStoreOrder);
        });

        this.$store.commit('shoppingCart/setOrders', arrayOfReservations);

        this.setDialog(false);
        this.$router.push({
          name: 'Booking',
        });
      } catch (error) {
        this.showProgress = false;
      }
    },
    loadData() {
      this.getShoppingLists();
      this.initLists();
    },
    setDialog(active) {
      this.dialog = active;
      this.$store.commit('shoppingList/setActive', active);
    },
    openDialog(dialogBoolean) {
      this.loadData();
      this.setDialog(dialogBoolean);
    },
    userOpensShoppingList() {
      this.loadData();
      this.setDialog(true);
    },
    // payShoppingList() {
    //   this.loading = true;
    //   this.userWantsToPayDialog = false;
    //
    //   const payload = {
    //     ShoppingListId: this.toPayShoppingList.ShoppingListId,
    //   };
    //
    //   doRequest(
    //     (response) => {
    //       this.loading = false;
    //       this.toPayShoppingList = {};
    //
    //       switch (response.data.Status) {
    //         case 'PartnerConfirmation':
    //           // Need to wait for partner's response
    //           console.log('Order needs to be confirmed by partner');
    //           this.successDialog = true;
    //           this.paymentMsg = this.$translate(
    //             'ORDER_WAIT_FOR_PARTNER_CONFIRMATION'
    //           );
    //           break;
    //         case 'Confirmed':
    //           // Order confirmed by payment method of DirectDebit or free of charge
    //           console.log('Order already been confirmed');
    //           if (
    //             response.data.PaymentMessage != null &&
    //             response.data.PaymentMessage.trim() !== ''
    //           ) {
    //             this.paymentMsg = response.data.PaymentMessage;
    //           } else {
    //             this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
    //           }
    //           this.successDialog = true;
    //           break;
    //         case 'WaitingForPayment':
    //           // Waiting for payment, redirect to mollie
    //           console.log('Order waiting for payment');
    //           if (
    //             response.data.PaymentUrl != null &&
    //             response.data.PaymentUrl.trim() !== ''
    //           ) {
    //             setTimeout(() => {
    //               this.redirectPayment(response.data.PaymentUrl);
    //             }, 0);
    //           } else {
    //             window.EventBus.fire('activateSnackBar', {
    //               text: this.$translate('BOOKING_FAIL'),
    //               color: 'error',
    //             });
    //           }
    //           break;
    //         default:
    //           console.log('Unknown order status: ', response.data.Status);
    //           this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
    //           this.successDialog = true;
    //           break;
    //       }
    //
    //       this.getShoppingLists();
    //     },
    //     (error) => {
    //       this.loading = false;
    //       this.getShoppingLists();
    //
    //       if (error.data.ErrorCode === 'E_DEL_002') {
    //         window.EventBus.fire('activateSnackBar', {
    //           text: this.$translate('E_DEL_002'),
    //           color: 'error',
    //         });
    //       } else {
    //         window.EventBus.fire('activateSnackBar', {
    //           text: this.$translate('BOOKING_FAIL'),
    //           color: 'error',
    //         });
    //       }
    //     },
    //     'payShoppingList',
    //     this.userId,
    //     payload
    //   );
    // },
    async payShoppingList() {
      this.loading = true;
      this.userWantsToPayDialog = false;

      const payload = {
        ShoppingListId: this.toPayShoppingList.ShoppingListId,
      };
      try {
        const response = await payShoppingList(payload);
        this.loading = false;
        this.toPayShoppingList = {};

        switch (response.data.Status) {
          case 'PartnerConfirmation':
            // Need to wait for partner's response
            console.log('Order needs to be confirmed by partner');
            this.successDialog = true;
            this.paymentMsg = this.$translate(
              'ORDER_WAIT_FOR_PARTNER_CONFIRMATION'
            );
            break;
          case 'Confirmed':
            // Order confirmed by payment method of DirectDebit or free of charge
            console.log('Order already been confirmed');
            if (
              response.data.PaymentMessage != null &&
              response.data.PaymentMessage.trim() !== ''
            ) {
              this.paymentMsg = response.data.PaymentMessage;
            } else {
              this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
            }
            this.successDialog = true;
            break;
          case 'WaitingForPayment':
            // Waiting for payment, redirect to mollie
            console.log('Order waiting for payment');
            if (
              response.data.PaymentUrl != null &&
              response.data.PaymentUrl.trim() !== ''
            ) {
              setTimeout(() => {
                this.redirectPayment(response.data.PaymentUrl);
              }, 0);
            } else {
              window.EventBus.fire('activateSnackBar', {
                text: this.$translate('BOOKING_FAIL'),
                color: 'error',
              });
            }
            break;
          default:
            console.log('Unknown order status: ', response.data.Status);
            this.paymentMsg = this.$translate('ORDER_COMPLETED_DEFAULT');
            this.successDialog = true;
            break;
        }
        this.getShoppingLists();
      } catch (error) {
        this.loading = false;
        this.getShoppingLists();
        if (error.data.ErrorCode === 'E_DEL_002') {
          window.EventBus.fire('activateSnackBar', {
            text: this.$translate('E_DEL_002'),
            color: 'error',
          });
        } else if (error.data.ErrorCode === 'E_SL_023') {
          let minimumOrderDate = error.data.MinimumOrderDate;
          let minimumOrderTime = error.data.MinimumOrderTime;
          this.orderStartMomentNotReachedMessage = utility.sprintf(
            this.$translate('ORDER_START_MOMENT'),
            minimumOrderDate, minimumOrderTime
          );
          this.orderStartMomentNotReachedDialog = true;
          console.log('ERROR! Received OrderStartMomentNotReached code E-SL_023. Message: ', this.orderStartMomentNotReachedMessage);
        } else {
          window.EventBus.fire('activateSnackBar', {
            text: this.$translate('BOOKING_FAIL'),
            color: 'error',
          });
        }
      }
    },
    goToMyActivities() {
      this.successDialog = false;
      this.setDialog(false);
      this.$router.push({
        name: 'UserSettings',
        params: { myOrders: true },
      });
    },
    redirectPayment(paymentUrl) {
      window.location.href = paymentUrl;
    },
    onSkipDelivery() {
      // User wants to skip the next delivery, show dialog to confirm this action
      this.showSkipDeliveryDialog = true;
    },
    async onSkipDeliveryConfirmed() {
      this.skipDeliveryBtnDisabled = true;
      this.showSkipDeliveryDialog = false;
      this.showProgress = true;

      const payload = {
        ShoppingListId:
          this.localShoppingLists[this.selectedShoppingList].ShoppingListId,
      };

      try {
        await skipDelivery(payload);
        this.showProgress = false;
        this.skipDeliveryBtnDisabled = false;
        this.getShoppingLists();
      } catch (error) {
        this.showProgress = false;
        this.skipDeliveryBtnDisabled = false;
      }
    },
    onSkipDeliveryCancelled() {
      this.showSkipDeliveryDialog = false;
    },
    onCreateBtnClick() {
      this.showNewListDialog = true;
    },
    isShoppingListAdmin(shoppingList) {
      return isShoppingListAdmin(shoppingList);
    },
    getShoppingListProductsTotalPrice(shoppingList) {
      let totalPrice = 0;

      shoppingList.Products.forEach((product) => {
        totalPrice += product.Price;
      });

      return totalPrice;
    },
    getShoppingListTotalPrice(shoppingList) {
      let totalPrice = 0;

      switch (shoppingList.Type) {
        case 'WishList':
          let deposit = 0;
          shoppingList.Products.forEach((product) => {
            if (product.TotalDepositValue > 0) {
              deposit = product.TotalDepositValue;
            }
            totalPrice += product.Price + deposit;
          });
          break;
        default:
          totalPrice = shoppingList.OrderGroup.TotalPrice;
          break;
      }

      totalPrice += this.getListDeliveryPriceInclVAT(shoppingList);

      if (this.pickupFee != null && this.pickupFee.isPossible()) {
        totalPrice += this.pickupFee.price;
      }

      return totalPrice;
    },
    getDepositValue(shoppingList) {
      let deposit = 0;
      if (shoppingList.OrderGroup) {
        for (const order of shoppingList.OrderGroup.Orders) {
          deposit += order.DepositValue;
        }
      }
      return deposit;
    },
    showMinSubscriptionValue(shoppingList) {
      if (shoppingList.Type !== 'Subscription') {
        return false;
      }
      const totalPrice = this.getShoppingListProductsTotalPrice(shoppingList);
      if (
        this.minSubscriptionValue > 0 &&
        totalPrice < this.minSubscriptionValue
      ) {
        return true;
      }
      return false;
    },
    onAddProduct(shoppingList) {
      // User wants to add more products to current list
      this.$store.commit('setCurrentSelectedShoppingList', shoppingList);

      if (this.$route.name !== 'Homepage') {
        this.$router.push({
          name: 'Homepage',
          params: { scrollTo: 'categoriesScroll' },
        });
      } else {
        scrollToElement('#allCategories');
      }

      this.setDialog(false);
    },
    hasValidSelection() {
      if (
        this.selectedShoppingList != null &&
        this.selectedShoppingList >= 0 &&
        this.localShoppingLists != null &&
        this.localShoppingLists.length > 0 &&
        this.selectedShoppingList < this.localShoppingLists.length
      ) {
        return true;
      }
      return false;
    },
    getAwardedGrowthVitaminCardsForList() {
      let listId = null;
      if (this.hasValidSelection()) {
        const list = this.localShoppingLists[this.selectedShoppingList];
        listId = list.ShoppingListId;
      }

      this.suggestions = [];
      getAwardedGrowthVitaminCards(listId)
        .then((response) => {
          if (response.data.Suggestions != null) {
            this.suggestions = [...response.data.Suggestions];
          }
        })
        .catch(() => {});
    },
    onSuggestionClick(shoppingList) {
      this.$store.commit('setCurrentSelectedShoppingList', shoppingList);
      this.setDialog(false);
    },
    isDeliveryDetailsValid(shoppingList) {
      if (
        shoppingList != null &&
        shoppingList.SubEventType != null &&
        (shoppingList.SubEventType === 'Delivery' ||
          shoppingList.SubEventType === 'Shiplee') &&
        shoppingList.DeliveryDetails != null &&
        shoppingList.DeliveryDetails.ResultSet != null &&
        shoppingList.DeliveryDetails.ResultSet.Price != null
      ) {
        return true;
      }
      return false;
    },
    getListDeliveryPrice(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }

      if (this.isProfUser) {
        return this.getListDeliveryPriceExclVAT(shoppingList);
      }

      return this.getListDeliveryPriceInclVAT(shoppingList);
    },
    getListDeliveryPriceInclVAT(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }
      return shoppingList.DeliveryDetails.ResultSet.Price;
    },
    getListDeliveryPriceExclVAT(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }

      const deliveryDetails = shoppingList.DeliveryDetails;
      return (
        deliveryDetails.ResultSet.Price - deliveryDetails.ResultSet.VatPrice
      );
    },
    getListDeliveryVATPrice(shoppingList) {
      if (!this.isDeliveryDetailsValid(shoppingList)) {
        return 0;
      }
      return shoppingList.DeliveryDetails.ResultSet.VatPrice;
    },
    getSortedItemDefValues,
    getNewProduct(product) {
      const item = getItemByDefs(product.Product, product.ItemDefObj);
      return {
        Product: product.Product,
        Item: item,
        ItemId: item.Id,
        Quantity: product.Quantity,
      };
    },
    goToSubscription() {
      this.$router.push({
        name: 'PartnerGroupSubscription',
      });
    },
    onCreateSubscription() {
      this.setDialog(false);
      this.goToSubscription();
    },
    onCreateShoppingList() {
      this.showNewListDialog = true;
    },
    onRemoveProduct(product) {
      const list = this.localShoppingLists[this.selectedShoppingList];

      const payload = {
        ShoppingListId: list.ShoppingListId,
        OrderId: product.OrderId,
        EventId: product.EventId,
        ProductId: product.ProductId,
        ItemId: product.ItemId,
      };

      if (product.TemporaryOrderItemId) {
        // Unique id of the selection for Wishlist (no order yet)
        payload.TemporaryOrderItemId = product.TemporaryOrderItemId;
      } else if (product.OrderItemId) {
        // Unique id of the selection for ShoppingList (order already created)
        payload.OrderItemId = product.OrderItemId;
      }

      this.showProgress = true;

      removeProductFromReservation(payload)
        .then((response) => {
          if (response.data.ShoppingList != null) {
            this.updateSelectedShoppingList(response.data.ShoppingList);
          } else {
            this.getShoppingLists();
          }
        })
        .catch(() => {
          window.EventBus.fire(
            'activateSnackBar',
            this.$translate('REMOVE_ITEM_ERROR')
          );
        })
        .finally(() => {
          this.showProgress = false;
        });
    },
    onAddOrSubtractsProduct(action, product) {
      console.log('product - from parent', product);
      switch (action) {
        case 'add':
          if (product.Product.Type === 'Weighted') {
            product.Quantity += product.Product.WeightedInfo.StepSize;
          } else {
            product.Quantity += 1;
          }
          break;
        case 'subtracts':
          if (product.Product.Type === 'Weighted') {
            product.Quantity -= product.Product.WeightedInfo.StepSize;
          } else {
            product.Quantity -= 1;
          }
          break;
        default:
          break;
      }

      this.onChangeProductItem(product);
    },
    onChangeProductItem(product) {
      // Add timeout id to the product to delay the backend request
      if (typeof product._timeoutId === 'number') {
        clearTimeout(product._timeoutId);
      }

      product._timeoutId = setTimeout(() => {
        this.changeItem(product);
      }, this.changeItemTimeOut);
    },
    changeItem(product) {
      const item = getItemByDefs(product.Product, product.ItemDefObj);
      if (
        product.Quantity === product._oldQuantity &&
        item.Id === product.ItemId
      ) {
        // Neither quantity nor item def is changed. No need to change item
        return;
      }

      const list = this.localShoppingLists[this.selectedShoppingList];

      const payload = {
        ShoppingListId: list.ShoppingListId,
        OrderId: product.OrderId,
        EventId: product.EventId,
        ProductId: product.ProductId,
        ItemId: product.ItemId,
        NewItemId: item.Id,
        Quantity: product.Quantity,
      };

      if (product.TemporaryOrderItemId) {
        // Unique id of the selection for Wishlist
        payload.TemporaryOrderItemId = product.TemporaryOrderItemId;
      } else if (product.OrderItemId) {
        // Unique id of the selection for ShoppingList
        payload.OrderItemId = product.OrderItemId;
      }

      this.showProgress = true;

      changeProductOnReservation(payload)
        .then(() => {})
        .catch((error) => {
          this.getShoppingLists();

          if (error.data.ErrorCode === 'E_PO_006') {
            window.EventBus.fire(
              'activateErrorDialog',
              this.$translate('E_PO_006_PRODUCT'),
              false
            );
          } else {
            window.EventBus.fire(
              'activateSnackBar',
              this.$translate('FAILED_ADD_SHOPPINGLIST')
            );
          }
        })
        .finally(() => {
          this.showProgress = false;
        });
    },
    updateSelectedShoppingList(list) {
      if (!this.hasValidSelection()) {
        return;
      }

      this.initProducts(list);
      this.localShoppingLists[this.selectedShoppingList] = { ...list };
      this.initSelectedList();
    },
    onUpdateSubscription() {
      this.confirmDialogTitle = this.$translate('UPDATE_SUBSCRIPTION');
      this.confirmDialogMsg = this.$translate('UPDATE_SUBSCRIPTION_DIALOG_MSG');
      this.confirmDialogPosBtnText = this.$translate('YES');
      this.confirmDialogNegBtnText = this.$translate('NO');
      this.confirmDialogPosBtnHandler = () => {
        this.updateSubscription();
      };
      this.showConfirmDialog = true;
    },
    updateSubscription() {
      this.updatingSubscription = true;
      const list = this.localShoppingLists[this.selectedShoppingList];
      const payload = { ShoppingListId: list.ShoppingListId };
      updateShoppingListProductsToCurrentOrder(payload)
        .then(() => {
          this.getShoppingLists();
          this.updatingSubscription = false;
        })
        .catch(() => {
          this.updatingSubscription = false;
        });
    },
    onResetSubscription() {
      this.confirmDialogTitle = this.$translate('RESET_SUBSCRIPTION');
      this.confirmDialogMsg = this.$translate('RESET_SUBSCRIPTION_DIALOG_MSG');
      this.confirmDialogPosBtnText = this.$translate('YES');
      this.confirmDialogNegBtnText = this.$translate('NO');
      this.confirmDialogPosBtnHandler = () => {
        this.resetSubscription();
      };
      this.showConfirmDialog = true;
    },
    resetSubscription() {
      this.resetingSubscription = true;
      const list = this.localShoppingLists[this.selectedShoppingList];
      const payload = { ShoppingListId: list.ShoppingListId };
      resetOrderToShoppingList(payload)
        .then(() => {
          this.getShoppingLists();
          this.resetingSubscription = false;
        })
        .catch(() => {
          this.resetingSubscription = false;
        });
    },
    onConfirmDialogPosBtnClick() {
      this.showConfirmDialog = false;
      if (typeof this.confirmDialogPosBtnHandler !== 'function') {
        this.confirmDialogPosBtnHandler = undefined;
        return;
      }

      this.confirmDialogPosBtnHandler();
    },
    onConfirmDialogNegBtnClick() {
      this.showConfirmDialog = false;

      if (typeof this.confirmDialogNegBtnHandler !== 'function') {
        this.confirmDialogNegBtnHandler = undefined;
        return;
      }

      this.confirmDialogNegBtnHandler();
    },
    onNewListCreated() {
      this.showNewListDialog = false;
      this.getShoppingLists();
    },
    getTotalVATPrice(list) {
      // Total vat price includes vat of products and delivery
      let totalVATPrice = 0;

      switch (list.Type) {
        case 'WishList':
          list.Products.forEach((product) => {
            const vatPrice = utility.getVatPrice(
              product.Price,
              product.Product.VatPercentage
            );
            totalVATPrice += vatPrice;
          });
          break;
        default:
          list.OrderGroup.Orders.forEach((order) => {
            // Vat from growth vitamin cards
            order.Discounts.forEach((discount) => {
              totalVATPrice += discount.VatPrice;
            });

            // Vat from products
            order.Products.forEach((product) => {
              totalVATPrice += product.ItemTotalVatPrice;
            });
          });
          break;
      }

      totalVATPrice += this.getListDeliveryVATPrice(list);

      if (this.pickupFee != null && this.pickupFee.isPossible()) {
        totalVATPrice += this.pickupFee.vatPrice;
      }

      return totalVATPrice;
    },
    getSubtotalPrice(list) {
      let price = 0;

      switch (list.Type) {
        case 'WishList':
          list.Products.forEach((product) => {
            price += this.getProductRetailPriceExclVat(product);
          });
          break;
        default:
          list.OrderGroup.Orders.forEach((order) => {
            order.Products.forEach((product) => {
              let amount = product.Quantity;
              if (isWeightedProduct(product)) {
                amount = product.Quantity / product.StepSize;
              }

              price +=
                amount * (product.ItemRetailPrice - product.ItemRetailVatPrice);
            });
          });
          break;
      }

      return price;
    },
    getTotalDiscount(list) {
      let totalDiscountPrice = 0;

      switch (list.Type) {
        case 'WishList':
          list.Products.forEach((product) => {
            if (this.isProfUser) {
              totalDiscountPrice +=
                product.PriceExclVat -
                this.getProductRetailPriceExclVat(product);
            } else {
              totalDiscountPrice +=
                product.Price - this.getProductRetailPriceInclVat(product);
            }
          });
          break;
        default:
          list.OrderGroup.Orders.forEach((order) => {
            // Discounts from growth vitamin cards
            order.Discounts.forEach((discount) => {
              if (this.isProfUser) {
                totalDiscountPrice += discount.Price - discount.VatPrice;
              } else {
                totalDiscountPrice += discount.Price;
              }
            });

            // Discounts from products
            order.Products.forEach((product) => {
              let amount = product.Quantity;
              if (isWeightedProduct(product)) {
                amount = product.Quantity / product.StepSize;
              }

              if (this.isProfUser) {
                let totalRetailPriceExclVat =
                  amount *
                  (product.ItemRetailPrice - product.ItemRetailVatPrice);
                totalDiscountPrice +=
                  product.ItemTotalPrice -
                  product.ItemTotalVatPrice -
                  totalRetailPriceExclVat;
              } else {
                let totalRetailPriceInclVat = amount * product.ItemRetailPrice;
                totalDiscountPrice +=
                  product.ItemTotalPrice - totalRetailPriceInclVat;
              }
            });
          });
          break;
      }

      if (totalDiscountPrice > 0) {
        totalDiscountPrice = 0;
      }

      return totalDiscountPrice;
    },
    getProductRetailPriceInclVat(product) {
      let price = product.RetailPrice;
      if (price === 0) {
        price = product.Price;
      }
      return price;
    },
    getProductRetailPriceExclVat(product) {
      let price = product.RetailPriceExVat;
      if (price === 0) {
        price = product.PriceExclVat;
      }
      return price;
    },
    getDiscountItems(list) {
      if (list.Type === 'WishList') {
        return [];
      }

      let discounts = [];

      if (list.OrderGroup != null) {
        list.OrderGroup.Orders.forEach((order) => {
          discounts.push(...order.Discounts);
        });
      }

      return discounts;
    },
    getPickupFee() {
      this.pickupFee = null;
      this.minPickupFeeAlert = '';

      const list = this.localShoppingLists[this.selectedShoppingList];
      if (list.Temporary || list.SubEventType !== 'PickUp') {
        return;
      }

      this.pickupFee = new PickupFeeJS(list.SubEventId);
      this.pickupFee.load({}, list.ShoppingListId).then(() => {
        this.minPickupFeeAlert = this.pickupFee.alertText();
      });
    },
    initPaymentMethod() {
      const list = this.localShoppingLists[this.selectedShoppingList];

      if (list == null || list.PaymentMethod == null) {
        this.selectedPaymentMethod = new PaymentMethod();
        return;
      }

      this.selectedPaymentMethod = new PaymentMethod(list.PaymentMethod, false);
    },
  },
};
</script>

<style lang="scss" scoped>
.products {
  max-height: 600px;
  overflow-y: auto;
  overflow-x: hidden;
}
</style>
